/**
 * The /design URL state (P0-9 deliverable 15): every switcher setting lives
 * in the search parameters, validated so a bad value falls back to its
 * default. Unknown keys are ignored. Nothing is kept in browser storage.
 * The default direction is Grimoire Noir, the GM's choice of 2026-09-28
 * (docs/PLAN.md section 2; the brief's default was Deep Atlas). The default
 * density is the app's, Table (UI r3c, DECISIONS 499).
 */
import { DEFAULT_DENSITY } from '@codex/ui/tokens';

export const DESIGN_PARAMS = {
  direction: ['atlas', 'ledger', 'noir'],
  mode: ['dark', 'light', 'system'],
  density: ['compact', 'comfortable', 'table'],
  cvd: ['none', 'protan', 'deutan', 'tritan'],
  motion: ['system', 'reduce'],
  forced: ['off', 'on'],
  view: ['edit', 'read'],
} as const;

export type DesignParam = keyof typeof DESIGN_PARAMS;

export type DesignSearch = { readonly [K in DesignParam]: (typeof DESIGN_PARAMS)[K][number] };

export const DESIGN_DEFAULTS: DesignSearch = {
  direction: 'noir',
  mode: 'system',
  density: DEFAULT_DENSITY,
  cvd: 'none',
  motion: 'system',
  forced: 'off',
  view: 'edit',
};

export const DESIGN_PARAM_KEYS = Object.keys(DESIGN_PARAMS) as DesignParam[];

function firstString(raw: unknown): string | undefined {
  if (typeof raw === 'string') return raw;
  if (Array.isArray(raw) && typeof raw[0] === 'string') return raw[0];
  return undefined;
}

/** Parses raw search parameters (strings, or a `URLSearchParams`) into a full, valid state. */
export function parseDesignSearch(
  raw: Readonly<Record<string, unknown>> | URLSearchParams,
): DesignSearch {
  const get = (key: string): unknown => (raw instanceof URLSearchParams ? raw.get(key) : raw[key]);
  const out: Record<string, string> = {};
  for (const key of DESIGN_PARAM_KEYS) {
    const value = firstString(get(key));
    const allowed = DESIGN_PARAMS[key] as readonly string[];
    out[key] = value !== undefined && allowed.includes(value) ? value : DESIGN_DEFAULTS[key];
  }
  return out as unknown as DesignSearch;
}

/** The parameters that differ from their defaults, for a clean URL. */
export function toUrlSearch(state: DesignSearch): Partial<Record<DesignParam, string>> {
  const out: Partial<Record<DesignParam, string>> = {};
  for (const key of DESIGN_PARAM_KEYS) {
    if (state[key] !== DESIGN_DEFAULTS[key]) out[key] = state[key];
  }
  return out;
}

/** The query string of a state (`?direction=noir&mode=light`, or empty). */
export function designQuery(state: DesignSearch): string {
  const params = new URLSearchParams(toUrlSearch(state) as Record<string, string>);
  const text = params.toString();
  return text ? `?${text}` : '';
}

/** The density the stage uses: the Run screen always uses Table density. */
export function stageDensity(state: DesignSearch, screen: string | null): DesignSearch['density'] {
  return screen === 'run' ? 'table' : state.density;
}
